import { type RefObject, useCallback, useEffect, useState } from 'react';

interface Dimension {
  width: {
    offset: number;
    client: number;
    scroll: number;
  };
  height: {
    offset: number;
    client: number;
    scroll: number;
  };
}

export function useGetDimension(ref: RefObject<HTMLElement>) {
  const [dimension, setDimension] = useState<Dimension>({
    width: {
      offset: 0,
      client: 0,
      scroll: 0,
    },
    height: {
      offset: 0,
      client: 0,
      scroll: 0,
    },
  });

  const getDimension = useCallback(() => {
    if (ref.current) {
      const { offsetWidth, clientWidth, scrollWidth, offsetHeight, clientHeight, scrollHeight } =
        ref.current;

      setDimension({
        width: {
          offset: offsetWidth,
          client: clientWidth,
          scroll: scrollWidth,
        },
        height: {
          offset: offsetHeight,
          client: clientHeight,
          scroll: scrollHeight,
        },
      });
    }
  }, [ref]);

  useEffect(() => {
    getDimension();

    const resizeObserver = new ResizeObserver(() => {
      getDimension();
    });

    const mutationObserver = new MutationObserver(() => {
      getDimension();
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
      mutationObserver.observe(ref.current, { childList: true, subtree: true });
    }

    return () => {
      if (ref.current) {
        resizeObserver.unobserve(ref.current);
        mutationObserver.disconnect();
      }
    };
  }, [ref, getDimension]);

  return dimension;
}
